import { useCompositorControls } from '@hoverinc/rendering-compositor-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CryptomatteRegionMenu } from 'src/modules/designStudio/components/DesignMenu/Menu/CryptomatteRegionMenu';
import { InteractiveRenderingStateContext } from 'src/modules/designStudio/providers';

/**
 * Allows selection of a region prior to transitioning to the CategoryMenu. If there is only 1 region,
 * the region selection is skipped and the CategoryMenu is immediately displayed. If there is a selected
 * region of the correct category, the region selection is skipped and CategoryMenu is immediately
 * displayed
 */
export const InteractiveRenderingRegionsMenu = ({
  category,
  categoryMenu: CategoryMenu,
  closeMenu,
}: {
  category: 'ROOFS' | 'TRIM' | 'WALLS';
  categoryMenu: React.JSXElementConstructor<{
    closeMenu: () => void;
    onBack?: () => void;
  }>;
  closeMenu: () => void;
}) => {
  const { deselectAllRegions, selectRegion, selectedRegion } =
    useCompositorControls();
  const { renderingFile } = useContext(InteractiveRenderingStateContext);

  const regionIds = useMemo(
    () =>
      renderingFile?.cryptomatteRegions?.regions
        .filter(region => region.category === category)
        .map(region => region.regionId) ?? [],
    [category, renderingFile],
  );

  const [hasMadeSelection, setHasMadeSelection] = useState(
    () => regionIds.includes(selectedRegion ?? '') || regionIds.length === 1,
  );

  useEffect(() => {
    if (regionIds.length === 1) {
      // When the menu is first opened, if there is only 1 region in the list of regions, select that region
      selectRegion(regionIds[0]);
    } else if (!regionIds.includes(selectedRegion ?? '')) {
      // When the menu is first opened, clear the current selection if the selected regions category does not match
      deselectAllRegions();
    }
    // Intentionally blank deps as this effect should only run a single time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Honor any selectedRegion changes while the regions menu is open. This allows the user to either click on a
  // highlighted region to finish selection or click on one of the region buttons to finish selection.
  useEffect(() => {
    if (regionIds.includes(selectedRegion ?? '')) {
      setHasMadeSelection(true);
    }
  }, [regionIds, selectedRegion]);

  const handleRegionSelection = (regionId: string) => {
    selectRegion(regionId);
    setHasMadeSelection(true);
  };

  const handleBack = () => {
    deselectAllRegions();
    setHasMadeSelection(false);
  };

  const handleClose = () => {
    deselectAllRegions();
    closeMenu();
  };

  if (!hasMadeSelection && regionIds.length > 1) {
    return (
      <CryptomatteRegionMenu
        category={category}
        closeMenu={handleClose}
        onRegionSelection={handleRegionSelection}
        regionIds={regionIds}
      />
    );
  }

  return (
    <CategoryMenu
      closeMenu={handleClose}
      onBack={regionIds.length === 1 ? undefined : handleBack}
    />
  );
};
